/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api";
import { Query_Keys } from "../lib/queryKeys";

export const PROFILE_PAGE_SIZE = 100;

// Circuit breaker only: at PROFILE_PAGE_SIZE per page this is 10,000 profiles, far beyond any
// real catalogue, so it never engages in practice — it just stops a runaway loop if the server
// ever reports a Total-Count it can't actually satisfy.
const MAX_PROFILE_PAGES = 100;

const normalizeProfile = (profile) => ({
  id: profile?.id,
  name: profile?.name || '',
  active: profile?.active !== false,
  applicable_roles: Array.isArray(profile?.applicable_roles) ? profile.applicable_roles : [],
  module_keys: Array.isArray(profile?.module_keys) ? profile.module_keys : [],
});

/**
 * Loads the permission profiles the picker in the user modal offers.
 *
 * Inactive profiles are kept in the list rather than filtered out here: one may still be assigned
 * to the user being edited and has to stay nameable. Choosing which ones may actually be picked is
 * `filterProfilesForRoles`, since that depends on roles the form changes live.
 *
 * @param {Object} options
 * @param {boolean} options.enabled fetch only once a modal actually needs the list
 */
export const usePermissionProfiles = ({ enabled = true } = {}) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: Query_Keys.permission_profiles({ source: 'user_module_access' }),
    queryFn: async () => {
      const rows = [];

      for (let page = 1; page <= MAX_PROFILE_PAGES; page += 1) {
        const response = await api.get('/v1/admin/permission_profiles', {
          params: { page, per_page: PROFILE_PAGE_SIZE, sort_by: 'name', sort_order: 'asc' },
        });
        const pageRows = Array.isArray(response?.data) ? response.data : [];
        rows.push(...pageRows);

        const totalCount = Number(response?.headers?.['total-count']);
        if (pageRows.length === 0 || Number.isNaN(totalCount) || rows.length >= totalCount) break;
      }

      return rows;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const profiles = rows.filter((profile) => profile?.id).map(normalizeProfile);

    return {
      profiles,
      profilesById: new Map(profiles.map((profile) => [profile.id, profile])),
      isLoading,
      isError,
      refetch,
    };
  }, [data, isLoading, isError, refetch]);
};
